import {styled, useTheme} from '@mui/material/styles';
import {Typography, ListItemButton, ListItemButtonProps, ListItemText} from '@mui/material';
import {ComponentType, useEffect, useState, useMemo} from 'react';

import {RecentFiltersSectionConfig} from 'filters/features/FilterDialog/types';
import {StoredFilter} from 'filters/features/types';

import {dayjs, isToday, isYesterday, dateFormatter} from 'utils/dateUtils';
import {RecentFiltersPrimaryAction} from 'filters/features/FilterDialog/sections/RecentFiltersSection/features/RecentFiltersPrimaryAction';
import {RecentFiltersSecondaryAction} from 'filters/features/FilterDialog/sections/RecentFiltersSection/features/RecentFiltersSecondaryAction';
import {useFilterActionsContext} from 'filters/contexts/FilterActionsProvider/FilterActionsProvider';

import {OverflownTooltip} from 'components/OverflownTooltip';
import {Placeholder} from 'components/Placeholder';
import NoResultsIcon from 'components/icons/placeholders/noResults.svg';
import LoadingIndicator from 'components/LoadingIndicator';

const SRoot = styled('div')(({theme}) => ({
  padding: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

export const useCreateRecentFiltersSection = (
  config: Omit<RecentFiltersSectionConfig, 'type' | 'recentId' | 'setRecentId'>
): RecentFiltersSectionConfig => {
  const [recentId, setRecentId] = useState<string | null>(null);

  return useMemo(
    () => ({
      recentId,
      setRecentId,
      footer: {
        primaryAction: RecentFiltersPrimaryAction,
        secondaryAction: RecentFiltersSecondaryAction,
        additionalActions: () => [],
      },
      ...config,
      type: 'recent-filters',
    }),
    [config, recentId]
  );
};

const SListItem = styled(ListItemButton as ComponentType<ListItemButtonProps>)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  minHeight: '58px',

  [theme.breakpoints.down('sm')]: {
    minHeight: '74px',
  },
}));

const UNKNOWN_KEY = 'Unknown';
const TODAY_KEY = 'Today';
const YESTERDAY_KEY = 'Yesterday';

export const RecentFiltersSection = ({recentId, setRecentId}: RecentFiltersSectionConfig) => {
  const {useStoredFilters} = useFilterActionsContext();
  const theme = useTheme();
  const {recentLoading, recentFilters} = useStoredFilters();

  const grouped = useMemo(() => {
    const map = (recentFilters || []).slice(1).reduce<Record<string, StoredFilter[]>>((acc, filter) => {
      const lastUsed = filter.lastUsed;

      if (!lastUsed) {
        acc[UNKNOWN_KEY] = acc[UNKNOWN_KEY] || [];
        acc[UNKNOWN_KEY].push(filter);
      } else if (isToday(lastUsed)) {
        acc[TODAY_KEY] = acc[TODAY_KEY] || [];
        acc[TODAY_KEY].push(filter);
      } else if (isYesterday(lastUsed)) {
        acc[YESTERDAY_KEY] = acc[YESTERDAY_KEY] || [];
        acc[YESTERDAY_KEY].push(filter);
      } else {
        const dateKey = dateFormatter(lastUsed);
        acc[dateKey] = acc[dateKey] || [];
        acc[dateKey].push(filter);
      }

      return acc;
    }, {});

    return Object.entries(map).sort((a, b) => {
      if (a[0] === TODAY_KEY) {
        return -1;
      }

      if (b[0] === TODAY_KEY) {
        return 1;
      }

      if (a[0] === YESTERDAY_KEY) {
        return -1;
      }

      if (b[0] === YESTERDAY_KEY) {
        return 1;
      }

      return new Date(b[0]).getTime() - new Date(a[0]).getTime();
    });
  }, [recentFilters]);

  useEffect(() => {
    return () => {
      setRecentId(null);
    };
  }, [setRecentId]);

  if (recentLoading) {
    return <LoadingIndicator />;
  }

  return (
    <SRoot>
      {!grouped.length ? (
        <Placeholder
          size='small'
          icon={<NoResultsIcon color={theme.palette.text.secondary} />}
          heading={`You don't have Recent filters`}
          subheading='Recently used filters will appear here.'
        />
      ) : (
        grouped.map(([group, items]) => (
          <div key={group}>
            <Typography component='div' color='text.secondary' variant='subtitle3' align='center'>
              {group}
            </Typography>
            {items.map(item => (
              <OverflownTooltip key={item.id} title={item.name}>
                {ref => (
                  <SListItem selected={item.id === recentId} onClick={() => setRecentId(item.id)}>
                    <ListItemText
                      primary={item.name}
                      secondary={item.lastUsed ? dayjs(item.lastUsed).format('M/D/YYYY - h:mm A') : undefined}
                      primaryTypographyProps={{variant: 'subtitle1', noWrap: true, ref}}
                      secondaryTypographyProps={{variant: 'subtitle3', color: 'text.secondary'}}
                    />
                  </SListItem>
                )}
              </OverflownTooltip>
            ))}
          </div>
        ))
      )}
    </SRoot>
  );
};
